import './PirateCallModule.scss'
import LSModule from "../../components/base/LSModule";
import {
    AppBar, Box, Button, Chip, Container, Divider,
    Grid, IconButton, List, ListItem, ListItemButton, ListItemText,
    Paper, Slide, Snackbar, TextField, Toolbar, Typography,
    Autocomplete, Card, CardContent, Fade, CircularProgress,
    Tooltip, Alert, useMediaQuery, Tab, Tabs,
    FormControlLabel, Checkbox
} from "@mui/material";
import { styled, alpha } from '@mui/material/styles';
import ExclusiveDatagrid from '../../components/ui/ExclusiveDatagrid';
import { GridActionsCellItem } from '@mui/x-data-grid';
import {
    Search, RemoveRedEye, ContentCopy, LocationOn,
    Phone, Work, Group, Person, Clear, FilterAlt,
    CalendarToday, Flag, Face, Close as CloseIcon,
    Info
} from '@mui/icons-material';
import { LysepAPI } from '../../contexts/ApiContext';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import PirateCallSearch from './components/PirateCallComponents/PirateCallSearch';
import LocationSearch from './components/PirateCallComponents/LocationSearch';
import SearchByLocation from './components/PirateCallComponents/SearchByLocation';
import { PERMISSIONS } from '../../constants/permissions';
import { usePermissions } from '../../contexts/PermissionContext';

// HOC to inject permissions into class component
const withPermissions = (Component) => {
    return (props) => {
        const permissions = usePermissions();
        return <Component {...props} permissions={permissions} />;
    };
};

// Styled components with improved mobile responsiveness
const StyledSearchBox = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1),
        transition: 'all 0.2s ease',
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        '&:hover': {
            backgroundColor: theme.palette.background.paper,
        },
        '&.Mui-focused': {
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 0 0 2px ${theme.palette.primary.main}',
        }
    },
    '& .MuiInputLabel-root': {
        transition: 'all 0.2s ease',
    },
    [theme.breakpoints.down('sm')]: {
        '& .MuiOutlinedInput-input': {
            padding: '14px 16px',
        }
    }
}));

const StyledCheckbox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    '& .MuiFormControlLabel-root': {
        marginRight: 0
    }
}));

const SearchChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
    borderRadius: theme.spacing(3),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    '& .MuiChip-deleteIcon': {
        color: theme.palette.error.main,
        '&:hover': {
            color: theme.palette.error.dark,
        }
    }
}));

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    [theme.breakpoints.down('sm')]: {
        borderRadius: theme.spacing(1),
    }
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    padding: '8px 24px',
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }
}));

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        transform: 'translateX(4px)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
    }
}));

const DetailSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .section-title': {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
            color: theme.palette.primary.main,
        }
    }
}));

/**
 * @namespace SearchModeBase
 */
const SearchModeBase = {
    INFO:0,
    TEL:1
}

class PirateCallModule extends LSModule {
    state = {
        isAddUserOpened: false,
        result: [],
        queryPool: {},
        loading: false,
        searchMode: SearchModeBase.INFO,
        activeFilters: [],
        snackBarOpen: false,
        snackBarMessage: '',
        snackBarSeverity: 'info',
        isSubmitting: false,
        tabIndex: 0,
    };

    constructor(props){
        super(props)
        this.fetch = this.fetch.bind(this)
    }

    componentDidMount() {
        // URL query parametrelerini oku ve formu doldur
        const urlParams = new URLSearchParams(window.location.search);
        
        // Tüm search fields'ları kontrol et
        const queryPoolFromUrl = {};
        const activeFiltersFromUrl = [];
        
        this.searchFields.forEach(field => {
            const value = urlParams.get(field.id);
            if (value && value.trim() !== '') {
                queryPoolFromUrl[field.id] = value.trim();
                activeFiltersFromUrl.push({ field: field.id, value: value.trim() });
            }
        });

        // Eğer URL'de parametre varsa formu doldur ve otomatik ara
        if (Object.keys(queryPoolFromUrl).length > 0) {
            // GSM varsa telefon moduna geç
            if (queryPoolFromUrl.GSM) {
                this.setState({
                    queryPool: { GSM: queryPoolFromUrl.GSM, GSM_strict: undefined },
                    activeFilters: [{ field: 'GSM', value: queryPoolFromUrl.GSM }],
                    searchMode: SearchModeBase.TEL
                }, () => {
                    // State güncellendikten sonra arama yap
                    this.fetch();
                });
            } else {
                // INFO modunda kal
                this.setState({
                    queryPool: queryPoolFromUrl,
                    activeFilters: activeFiltersFromUrl,
                    searchMode: SearchModeBase.INFO
                }, () => {
                    // State güncellendikten sonra arama yap
                    this.fetch();
                });
            }
        }
    }

    queryPoolTextValue(variable){
        return this.state[variable]
    }

    queryPoolCheckValue(variable){
        return  this.state.queryPool[variable+"_strict"] === undefined ? true : this.state.queryPool[variable+"_strict"]
    }

    queryPoolTextState(variable,value){
        let injection = {}
        injection[variable] = value === "" ? undefined : value
        this.setState(state=>{
            return {
                ...state,
                searchMode:variable === "GSM" && value !== "" && value !== undefined ? SearchModeBase.TEL : SearchModeBase.INFO,
                queryPool:{
                    ...state.queryPool,
                    ...injection
                }
            };
        })
    }

    queryPoolCheckboxState(variable,value){
        let injection = {}
        injection[variable+"_strict"] = value ? undefined : value
        this.setState(state=>{
            return {
                ...state,
                queryPool:{
                    ...state.queryPool,
                    ...injection
                }
            };
        })
    }

    showNotification = (message, severity = 'info') => {
        this.setState({
            snackBarOpen: true,
            snackBarMessage: message,
            snackBarSeverity: severity
        });
    };

    debouncedSearch = debounce(() => {
        this.fetch();
    }, 500);

    handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.handleSubmit(event);
        }
    };

    handleSubmit = async (event) => {
        event.preventDefault();
        
        if (this.state.isSubmitting || this.state.loading) {
            return;
        }

        if (!this.hasValidSearchCriteria()) {
            this.showNotification('Lütfen en az bir arama kriteri girin', 'warning');
            return;
        }

        this.setState({ isSubmitting: true, loading: true });

        try {
            await this.fetch();
        } finally {
            this.setState({ isSubmitting: false, loading: false });
        }
    };

    handleInputChange = (field, value) => {
        // Doğum tarihi formatını düzenle
        if (field === 'DOGUMTARIHI' && value) {
            // Sadece sayıları ve noktaları kabul et
            value = value.replace(/[^\d.]/g, '');
            
            // Formatı dd.mm.yyyy olarak düzenle
            if (value.length > 2 && !value.includes('.')) {
                value = value.slice(0, 2) + '.' + value.slice(2);
            }
            if (value.length > 5 && value.split('.').length === 2) {
                value = value.slice(0, 5) + '.' + value.slice(5);
            }
            
            // Maksimum uzunluğu kontrol et
            if (value.length > 10) {
                value = value.slice(0, 10);
            }
        }

        const newQueryPool = {
            ...this.state.queryPool,
            [field]: value || undefined
        };

        // If the field is GSM, update search mode and clear other fields if value exists
        if (field === 'GSM') {
            if (value) {
                // If GSM has a value, clear all other fields
                Object.keys(this.state.queryPool).forEach(key => {
                    if (key !== 'GSM') {
                        delete newQueryPool[key];
                    }
                });
                this.setState({ 
                    queryPool: newQueryPool,
                    searchMode: SearchModeBase.TEL,
                    activeFilters: [{ field: 'GSM', value }]
                });
            } else {
                // If GSM is cleared, reset search mode
                this.setState({ 
                    queryPool: newQueryPool,
                    searchMode: SearchModeBase.INFO,
                    activeFilters: this.state.activeFilters.filter(f => f.field !== 'GSM')
                });
            }
        } else {
            // If GSM search is active, don't allow other fields to be set
            if (this.state.searchMode === SearchModeBase.TEL) {
                return;
            }
            
            this.setState({ 
                queryPool: newQueryPool,
                activeFilters: value 
                    ? [...this.state.activeFilters.filter(f => f.field !== field), { field, value }]
                    : this.state.activeFilters.filter(f => f.field !== field)
            });
        }
    };

    addFilter = (field, value) => {
        const activeFilters = this.state.activeFilters.filter(f => f.field !== field);
        activeFilters.push({ field, value });
        this.setState({ activeFilters });
    };

    removeFilter = (field) => {
        const activeFilters = this.state.activeFilters.filter(f => f.field !== field);
        this.setState({ activeFilters });
    };

    clearAllFilters = () => {
        this.setState({
            queryPool: {},
            activeFilters: [],
            result: []
        });
    };

    searchFields = [
        { id: 'TC', label: 'TC Kimlik No', icon: <Person />, required: true },
        { id: 'ADI', label: 'Adı', icon: <Person /> },
        { id: 'SOYADI', label: 'Soyadı', icon: <Person /> },
        { id: 'DOGUMTARIHI', label: 'Doğum Tarihi', icon: <CalendarToday /> },
        { id: 'ANNEADI', label: 'Anne Adı', icon: <Face /> },
        { id: 'ANNETC', label: 'Anne TC', icon: <Person /> },
        { id: 'BABAADI', label: 'Baba Adı', icon: <Face /> },
        { id: 'BABATC', label: 'Baba TC', icon: <Person /> },
        { id: 'NUFUSIL', label: 'Nüfus İl', icon: <LocationOn /> },
        { id: 'NUFUSILCE', label: 'Nüfus İlçe', icon: <LocationOn /> },
        { id: 'UYRUK', label: 'Uyruk', icon: <Flag /> },
        { id: 'GSM', label: 'Telefon', icon: <Phone /> }
    ];

    fetch = async () => {
        if (Object.keys(this.state.queryPool).length === 0) {
            this.showNotification('Please enter search criteria', 'warning');
            return;
        }

        let processedQueryPool = {};
        switch (this.state.searchMode) {
            case SearchModeBase.INFO:
                processedQueryPool = {
                    ...this.state.queryPool,
                    GSM: undefined,
                    GSM_strict: undefined
                };
                break;
            case SearchModeBase.TEL:
                processedQueryPool = {
                    GSM: this.state.queryPool.GSM,
                    GSM_strict: this.state.queryPool.GSM_strict
                };
                break;
            default:
                break;
        }

        try {
        switch (this.state.searchMode) {
            case SearchModeBase.INFO:
                    const response = await LysepAPI.post("/api/piratecall/query", processedQueryPool);
                    console.log('Full Response:', response); // Debug log
                    
                    // Safely access nested properties
                    const results = response?.data?.results || [];
                    this.setState({ result: results });
                    this.showNotification(`Found ${results.length} results`, 'success');
                break;

            case SearchModeBase.TEL:
                    const telResponse = await LysepAPI.post("/api/piratecall/query/gsm", processedQueryPool);
                    console.log('Full Tel Response:', telResponse); // Debug log
                    
                    // Safely access nested properties
                    const telResults = telResponse?.data?.results || [];
                    this.setState({ result: telResults });
                    this.showNotification(`Found ${telResults.length} results`, 'success');
                break;

            default:
                break;
        }
        } catch (error) {
            console.error('Search error:', error);
            console.error('Error response:', error.response);
            this.setState({ result: [] });
            this.showNotification(
                error.response?.data?.message || error.message || 'An error occurred while searching', 
                'error'
            );
        } finally {
            this.setState({ isSubmitting: false, loading: false });
        }
    };

    loadingScreen(){
        return (<Fade
            in={this.state.loading}
            style={{
                transitionDelay: this.state.loading ? '800ms' : '0ms',
            }}
            unmountOnExit
        >
            <CircularProgress color="inherit" />
        </Fade>)
    }

    handleTabChange = (event, newValue) => {
        this.setState({ tabIndex: newValue });
    };

    render() {
        const { hasPermission } = this.props.permissions || {};
        
        // Filter search fields based on permissions
        const filteredSearchFields = this.searchFields.filter(field => {
            if (field.id === 'GSM') {
                return hasPermission ? hasPermission(PERMISSIONS.PIRATECALL_QUERY_GSM) : true;
            }
            return hasPermission ? hasPermission(PERMISSIONS.PIRATECALL_QUERY) : true;
        });

        return (
            <Container 
                maxWidth="xl" 
                sx={{ 
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    pb: 0,
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ 
                    flex: 1,
                    py: { xs: 2, md: 4 },
                    px: { xs: 1, sm: 2, md: 3 },
                    overflow: 'auto',
                    height: '100%',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#fd7000',
                        borderRadius: '4px',
                        '&:hover': {
                            background: '#e66500',
                        },
                    },
                }}>
                    <Typography 
                        variant="h4" 
                        gutterBottom 
                        align="center" 
                        sx={{ 
                            mb: { xs: 2, md: 4 },
                            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
                            fontWeight: 600,
                            color: 'primary.main'
                        }}
                    >
                        Piratecall
                    </Typography>

                    <Tabs
                        value={this.state.tabIndex}
                        onChange={this.handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ mb: 3 }}
                    >
                        {hasPermission && hasPermission(PERMISSIONS.PIRATECALL_QUERY) && (
                            <Tab label="Kişi Arama" />
                        )}
                        {hasPermission && hasPermission(PERMISSIONS.PIRATECALL_QUERY_ADDRESS) && (
                            <Tab label="Adres Arama" />
                        )}
                    </Tabs>

                    {this.state.tabIndex === 0 && hasPermission && hasPermission(PERMISSIONS.PIRATECALL_QUERY) && (
                        <PirateCallSearch
                            searchFields={filteredSearchFields}
                            queryPool={this.state.queryPool}
                            searchMode={this.state.searchMode}
                            isSubmitting={this.state.isSubmitting}
                            loading={this.state.loading}
                            activeFilters={this.state.activeFilters}
                            onInputChange={this.handleInputChange}
                            onCheckboxChange={this.queryPoolCheckboxState.bind(this)}
                            onKeyPress={this.handleKeyPress}
                            onSubmit={this.handleSubmit}
                            onClearAll={this.clearAllFilters}
                            onRemoveFilter={this.removeFilter}
                            results={this.state.result}
                            hasValidSearchCriteria={this.hasValidSearchCriteria()}
                        />
                    )}
                    {this.state.tabIndex === 1 && hasPermission && hasPermission(PERMISSIONS.PIRATECALL_QUERY_ADDRESS) && (
                        <SearchByLocation />
                    )}
                    {this.state.tabIndex === 2 && (
                        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="h6">Bu araç yakında eklenecek.</Typography>
                        </Box>
                    )}

                    <Snackbar
                        open={this.state.snackBarOpen}
                        autoHideDuration={4000}
                        onClose={() => this.setState({ snackBarOpen: false })}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            severity={this.state.snackBarSeverity}
                            onClose={() => this.setState({ snackBarOpen: false })}
                            sx={{ width: '100%', borderRadius: 2 }}
                            variant="filled"
                        >
                            {this.state.snackBarMessage}
                        </Alert>
                    </Snackbar>

                    {this.loadingScreen()}
                </Box>
            </Container>
        );
    }

    hasValidSearchCriteria() {
        // En az bir arama kriteri girilmiş olmalı
        return Object.values(this.state.queryPool).some(value => value && value.trim() !== '');
    }
}

export default withPermissions(PirateCallModule);